//-tech-debt-react-query;
import { useQueryClient } from '@tanstack/react-query';
import { addCiscoProduct, deleteCiscoProduct, refreshProduct, updateCiscoProduct } from "../support/slices/productsSlice";
import { store } from "../support/store";
import { PartialWithId } from "../types/base-types";
import { CacheKey } from "../types/cache-keys";
import {
  Product,
  ProductAppliance,
  ProductComponent,
  ProductFeature,
  ProductLicense,
  ProductUseCase,
  ProductVersion,
  ProductFeatureSuccessCriteria,
} from "../types/product";
import {
  useAddItemMutation,
  useDeleteItemMutation,
  useFetchItem,
  useFetchItems,
  useUpdateItemMutation,
  validId,
} from "./base-hooks";

export type ProductFeatureVersionLink = {
  id: number;
  product_feature: ProductFeature["id"] | ProductFeature;
  version: ProductVersion["id"] | ProductVersion;
};

// Note: product fetch and mutations are currently only used for cisco products. Non cisco products are read from slice only
const useFetchSingleProduct = (id, params?, options?) =>
  useFetchItem<Product>(
    "db/product",
    id,
    CacheKey.CiscoProducts,
    { ...params, detailed: true },
    {
      ...options,
      enabled: typeof options?.enabled === "boolean"
        ? options.enabled && validId(id)
        : validId(id),
    }
  );

const useFetchCiscoProducts = (params?, options?) =>
  useFetchItems<Product[]>(
    "db/product",
    CacheKey.CiscoProducts,
    { ...params, vendor__is_cisco: true },
    { ...options, enabled: true }
  );

// by default, we can only add a cisco product
const useAddProductMutation = () =>
  useAddItemMutation<Product>(
    "db/product",
    () => [CacheKey.CiscoProducts],
    {
      onSuccess: (data) => {
        const state = store.getState();
        if (state?.products?.isCiscoProductsInitialized && data.vendor?.is_cisco) {
          store.dispatch(addCiscoProduct(data));
        }
      },
    },
    { detailed: true }
  );

const useUpdateProductMutation = () =>
  useUpdateItemMutation<PartialWithId<Product>, Product>(
    "db/product",
    (payload) => [CacheKey.CiscoProducts, payload.id],
    {
      onSuccess: (data) => {
        const state = store.getState();
        if (state?.products?.isCiscoProductsInitialized) {
          store.dispatch(updateCiscoProduct(data));
        }
      },
    },
    { detailed: true }
  );

const useDeleteProductMutation = () =>
  useDeleteItemMutation<PartialWithId<Product>>(
    "db/product",
    (payload) => [CacheKey.CiscoProducts, payload.id],
    {
      onSuccess: (_, variables) => {
        const state = store.getState();
        if (state?.products?.isCiscoProductsInitialized) {
          store.dispatch(deleteCiscoProduct(variables.id));
        }
      },
    }
  );

// Product Component
const useFetchProductComponents = (productId: Product["id"], params?, options?) =>
  useFetchItems<ProductComponent[]>(
    "db/product-component",
    () => [CacheKey.ProductComponent, CacheKey.Product, productId],
    { ...params, product: productId },
    {
      ...options,
      enabled: typeof options?.enabled === "boolean"
        ? options.enabled && validId(productId)
        : validId(productId),
    }
  );

const useFetchSingleProductComponent = (
  productId: Product["id"],
  componentId: ProductComponent["id"],
  params?,
  options?
) =>
  useFetchItem<ProductComponent>(
    "db/product-component",
    componentId,
    () => [CacheKey.ProductComponent, CacheKey.Product, productId],
    { ...params, detailed: true },
    {
      ...options,
      enabled: typeof options?.enabled === "boolean"
        ? options.enabled && validId(productId) && validId(componentId)
        : validId(productId) && validId(componentId),
    }
  );

const useAddProductComponentMutation = (productId: Product["id"]) => {
  const queryClient = useQueryClient();

  return useAddItemMutation<ProductComponent>(
    "db/product-component",
    () => [CacheKey.ProductComponent, CacheKey.Product, productId],
    {
      onSuccess: async () => {
        await queryClient.refetchQueries({ queryKey: [CacheKey.CiscoProducts, String(productId)] });

        const state = store.getState();
        if (state?.products?.isCiscoProductsInitialized) {
          const product = queryClient.getQueryData<Product>([CacheKey.CiscoProducts, String(productId)]);
          if (product) {
            store.dispatch(refreshProduct(product));
          }
        }
      },
    }
  );
};

const useUpdateProductComponentMutation = (productId: Product["id"]) => {
  const queryClient = useQueryClient();

  return useUpdateItemMutation<ProductComponent>(
    "db/product-component",
    (payload) => [CacheKey.ProductComponent, CacheKey.Product, productId, payload.id],
    {
      onSuccess: async () => {
        await queryClient.refetchQueries({ queryKey: [CacheKey.CiscoProducts, String(productId)] });

        const state = store.getState();
        if (state?.products?.isCiscoProductsInitialized) {
          const product = queryClient.getQueryData<Product>([CacheKey.CiscoProducts, String(productId)]);
          if (product) {
            store.dispatch(refreshProduct(product));
          }
        }
      },
    },
    { detailed: true }
  );
};

const useDeleteProductComponentMutation = (productId: Product["id"]) => {
  const queryClient = useQueryClient();

  return useDeleteItemMutation<PartialWithId<ProductComponent>>(
    "db/product-component",
    (payload) => [CacheKey.ProductComponent, CacheKey.Product, productId, payload.id],
    {
      onSuccess: async () => {
        await queryClient.refetchQueries({ queryKey: [CacheKey.CiscoProducts, String(productId)] });

        const state = store.getState();
        if (state?.products?.isCiscoProductsInitialized) {
          const product = queryClient.getQueryData<Product>([CacheKey.CiscoProducts, String(productId)]);
          if (product) {
            store.dispatch(refreshProduct(product));
          }
        }
      },
    }
  );
};

// Product Use Case
const useFetchProductUseCases = (productId: Product["id"], params?, options?) =>
  useFetchItems<ProductUseCase[]>(
    "db/product-use-case",
    () => [CacheKey.ProductUseCase, CacheKey.Product, productId],
    { ...params, product: productId },
    {
      ...options,
      enabled: typeof options?.enabled === "boolean"
        ? options.enabled && validId(productId)
        : validId(productId),
    }
  );

const useFetchSingleProductUseCase = (
  productId: Product["id"],
  useCaseId: ProductUseCase["id"],
  params?,
  options?
) =>
  useFetchItem<ProductUseCase>(
    "db/product-use-case",
    useCaseId,
    () => [CacheKey.ProductUseCase, CacheKey.Product, productId],
    { ...params, detailed: true },
    {
      ...options,
      enabled: typeof options?.enabled === "boolean"
        ? options.enabled && validId(productId) && validId(useCaseId)
        : validId(productId) && validId(useCaseId),
    }
  );

const useAddProductUseCaseMutation = (productId: Product["id"]) => {
  const queryClient = useQueryClient();

  return useAddItemMutation<ProductUseCase>(
    "db/product-use-case",
    () => [CacheKey.ProductUseCase, CacheKey.Product, productId],
    {
      onSuccess: async () => {
        await queryClient.refetchQueries({ queryKey: [CacheKey.CiscoProducts, String(productId)] });

        const state = store.getState();
        if (state?.products?.isCiscoProductsInitialized) {
          const product = queryClient.getQueryData<Product>([CacheKey.CiscoProducts, String(productId)]);
          if (product) {
            store.dispatch(refreshProduct(product));
          }
        }
      },
    }
  );
};

const useUpdateProductUseCaseMutation = (productId: Product["id"]) => {
  const queryClient = useQueryClient();

  return useUpdateItemMutation<ProductUseCase>(
    "db/product-use-case",
    (payload) => [CacheKey.ProductUseCase, CacheKey.Product, productId, payload.id],
    {
      onSuccess: async () => {
        await queryClient.refetchQueries({ queryKey: [CacheKey.CiscoProducts, String(productId)] });

        const state = store.getState();
        if (state?.products?.isCiscoProductsInitialized) {
          const product = queryClient.getQueryData<Product>([CacheKey.CiscoProducts, String(productId)]);
          if (product) {
            store.dispatch(refreshProduct(product));
          }
        }
      },
    }
  );
};

const useDeleteProductUseCaseMutation = (productId: Product["id"]) => {
  const queryClient = useQueryClient();

  return useDeleteItemMutation<PartialWithId<ProductUseCase>>(
    "db/product-use-case",
    (payload) => [CacheKey.ProductUseCase, CacheKey.Product, productId, payload.id],
    {
      onSuccess: async () => {
        await queryClient.refetchQueries({ queryKey: [CacheKey.CiscoProducts, String(productId)] });

        const state = store.getState();
        if (state?.products?.isCiscoProductsInitialized) {
          const product = queryClient.getQueryData<Product>([CacheKey.CiscoProducts, String(productId)]);
          if (product) {
            store.dispatch(refreshProduct(product));
          }
        }
      },
    }
  );
};

// Product Features
const PRODUCT_FEATURE_URL = "db/product-feature";

const useFetchAllProductFeatures = (params?, options?) =>
  useFetchItems<ProductFeature[]>(
    PRODUCT_FEATURE_URL,
    CacheKey.ProductFeature,
    { ...params, size: 2000 },
    options
  );

const useFetchProductFeatures = (productId: Product["id"], params?, options?) =>
  useFetchItems<ProductFeature[]>(
    PRODUCT_FEATURE_URL,
    () => [CacheKey.ProductFeature, CacheKey.Product, productId],
    { ...params, product: productId },
    {
      ...options,
      enabled: typeof options?.enabled === "boolean"
        ? options.enabled && validId(productId)
        : validId(productId),
    }
  );

const useFetchSingleProductFeature = (
  productId: Product["id"],
  featureId: ProductFeature["id"],
  params?,
  options?
) =>
  useFetchItem<ProductFeature>(
    PRODUCT_FEATURE_URL,
    featureId,
    () => [CacheKey.ProductFeature, CacheKey.Product, productId],
    { ...params, detailed: true },
    {
      ...options,
      enabled: typeof options?.enabled === "boolean"
        ? options.enabled && validId(productId) && validId(featureId)
        : validId(productId) && validId(featureId),
    }
  );

const useAddProductFeatureMutation = (productId: Product["id"]) => {
  const queryClient = useQueryClient();

  return useAddItemMutation<ProductFeature>(
    PRODUCT_FEATURE_URL,
    () => [CacheKey.ProductFeature, CacheKey.Product, productId],
    {
      onSuccess: async () => {
        await queryClient.refetchQueries({ queryKey: [CacheKey.CiscoProducts, String(productId)] });

        const state = store.getState();
        if (state?.products?.isCiscoProductsInitialized) {
          const product = queryClient.getQueryData<Product>([CacheKey.CiscoProducts, String(productId)]);
          if (product) {
            store.dispatch(refreshProduct(product));
          }
        }
      },
    }
  );
};

const useUpdateProductFeatureMutation = (productId: Product["id"]) => {
  const queryClient = useQueryClient();

  return useUpdateItemMutation<ProductFeature>(
    PRODUCT_FEATURE_URL,
    (payload) => [CacheKey.ProductFeature, CacheKey.Product, productId, payload.id],
    {
      onSuccess: async () => {
        await queryClient.refetchQueries({ queryKey: [CacheKey.CiscoProducts, String(productId)] });

        const state = store.getState();
        if (state?.products?.isCiscoProductsInitialized) {
          const product = queryClient.getQueryData<Product>([CacheKey.CiscoProducts, String(productId)]);
          if (product) {
            store.dispatch(refreshProduct(product));
          }
        }
      },
    }
  );
};

const useDeleteProductFeatureMutation = (productId: Product["id"]) => {
  const queryClient = useQueryClient();

  return useDeleteItemMutation<PartialWithId<ProductFeature>>(
    PRODUCT_FEATURE_URL,
    (payload) => [CacheKey.ProductFeature, CacheKey.Product, productId, payload.id],
    {
      onSuccess: async () => {
        await queryClient.refetchQueries({ queryKey: [CacheKey.CiscoProducts, String(productId)] });

        const state = store.getState();
        if (state?.products?.isCiscoProductsInitialized) {
          const product = queryClient.getQueryData<Product>([CacheKey.CiscoProducts, String(productId)]);
          if (product) {
            store.dispatch(refreshProduct(product));
          }
        }
      },
    }
  );
};

const PRODUCT_FEATURE_SUCCESS_CRITERIA_URL = "db/product-feature-success-criteria";

const useFetchProductFeatureSuccessCriteria = (
  featureId: ProductFeature["id"],
  params?,
  options?
) =>
  useFetchItems<ProductFeatureSuccessCriteria[]>(
    PRODUCT_FEATURE_SUCCESS_CRITERIA_URL,
    () => [CacheKey.ProductFeatureSuccessCriteria, CacheKey.ProductFeature, featureId],
    { ...params, product_feature: featureId },
    {
      ...options,
      enabled:
        typeof options?.enabled === "boolean"
          ? options.enabled && validId(featureId)
          : validId(featureId),
    }
  );

const useAddProductFeatureSuccessCriteriaMutation = (
  productId: Product["id"],
  featureId: ProductFeature["id"]
) => {
  const queryClient = useQueryClient();

  return useAddItemMutation<ProductFeatureSuccessCriteria>(
    PRODUCT_FEATURE_SUCCESS_CRITERIA_URL,
    () => [CacheKey.ProductFeatureSuccessCriteria, CacheKey.ProductFeature, featureId],
    {
      onSuccess: async () => {
        await queryClient.refetchQueries({
          queryKey: [
            CacheKey.ProductFeatureSuccessCriteria,
            CacheKey.ProductFeature,
            featureId,
          ],
        });
        await queryClient.refetchQueries({ queryKey: [CacheKey.CiscoProducts, String(productId)] });

        const state = store.getState();
        if (state?.products?.isCiscoProductsInitialized) {
          const product = queryClient.getQueryData<Product>([CacheKey.CiscoProducts, String(productId)]);
          if (product) {
            store.dispatch(refreshProduct(product));
          }
        }
      },
    }
  );
};

const useUpdateProductFeatureSuccessCriteriaMutation = (
  productId: Product["id"],
  featureId: ProductFeature["id"]
) => {
  const queryClient = useQueryClient();

  return useUpdateItemMutation<ProductFeatureSuccessCriteria>(
    PRODUCT_FEATURE_SUCCESS_CRITERIA_URL,
    (payload) => [
      CacheKey.ProductFeatureSuccessCriteria,
      CacheKey.ProductFeature,
      featureId,
      payload.id,
    ],
    {
      onSuccess: async () => {
        await queryClient.refetchQueries({
          queryKey: [
            CacheKey.ProductFeatureSuccessCriteria,
            CacheKey.ProductFeature,
            featureId,
          ],
        });
        await queryClient.refetchQueries({ queryKey: [CacheKey.CiscoProducts, String(productId)] });

        const state = store.getState();
        if (state?.products?.isCiscoProductsInitialized) {
          const product = queryClient.getQueryData<Product>([CacheKey.CiscoProducts, String(productId)]);
          if (product) {
            store.dispatch(refreshProduct(product));
          }
        }
      },
    }
  );
};

const useDeleteProductFeatureSuccessCriteriaMutation = (
  productId: Product["id"],
  featureId: ProductFeature["id"]
) => {
  const queryClient = useQueryClient();

  return useDeleteItemMutation<PartialWithId<ProductFeatureSuccessCriteria>>(
    PRODUCT_FEATURE_SUCCESS_CRITERIA_URL,
    (payload) => [
      CacheKey.ProductFeatureSuccessCriteria,
      CacheKey.ProductFeature,
      featureId,
      payload.id,
    ],
    {
      onSuccess: async () => {
        await queryClient.refetchQueries({
          queryKey: [
            CacheKey.ProductFeatureSuccessCriteria,
            CacheKey.ProductFeature,
            featureId,
          ],
        });
        await queryClient.refetchQueries({ queryKey: [CacheKey.CiscoProducts, String(productId)] });

        const state = store.getState();
        if (state?.products?.isCiscoProductsInitialized) {
          const product = queryClient.getQueryData<Product>([CacheKey.CiscoProducts, String(productId)]);
          if (product) {
            store.dispatch(refreshProduct(product));
          }
        }
      },
    }
  );
};

// Product Version
const PRODUCT_VERSION_URL = "db/version";
const PRODUCT_FEATURE_VERSION_URL = "db/product-feature-version";

const useFetchProductVersions = (productId: Product["id"], params?, options?) =>
  useFetchItems<ProductVersion[]>(
    PRODUCT_VERSION_URL,
    () => [CacheKey.ProductVersion, CacheKey.Product, productId],
    { ...params, product: productId },
    {
      ...options,
      enabled: typeof options?.enabled === "boolean"
        ? options.enabled && validId(productId)
        : validId(productId),
    }
  );

const useFetchProductFeatureVersions = (
  featureId: ProductFeature["id"],
  params?,
  options?
) =>
  useFetchItems<ProductFeatureVersionLink[]>(
    PRODUCT_FEATURE_VERSION_URL,
    () => [CacheKey.ProductFeatureVersion, CacheKey.ProductFeature, featureId],
    { ...params, product_feature: featureId },
    {
      ...options,
      enabled: typeof options?.enabled === "boolean"
        ? options.enabled && validId(featureId)
        : validId(featureId),
    }
  );

const useFetchProductVersionFeatures = (
  versionId: ProductVersion["id"],
  params?,
  options?
) =>
  useFetchItems<ProductFeatureVersionLink[]>(
    PRODUCT_FEATURE_VERSION_URL,
    () => [CacheKey.ProductFeatureVersion, CacheKey.ProductVersion, versionId],
    { ...params, version: versionId },
    {
      ...options,
      enabled: typeof options?.enabled === "boolean"
        ? options.enabled && validId(versionId)
        : validId(versionId),
    }
  );

const useAddProductFeatureVersionMutation = (
  productId: Product["id"],
  versionId?: ProductVersion["id"]
) => {
  const queryClient = useQueryClient();

  return useAddItemMutation<ProductFeatureVersionLink>(
    PRODUCT_FEATURE_VERSION_URL,
    () => [CacheKey.ProductFeatureVersion, CacheKey.ProductVersion, versionId],
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: [CacheKey.ProductFeatureVersion] });
        await queryClient.invalidateQueries({ queryKey: [CacheKey.ProductVersion, CacheKey.Product, productId] });
        await queryClient.invalidateQueries({ queryKey: [CacheKey.ProductFeature, CacheKey.Product, productId] });
        await queryClient.refetchQueries({ queryKey: [CacheKey.CiscoProducts, String(productId)] });

        const state = store.getState();
        if (state?.products?.isCiscoProductsInitialized) {
          const product = queryClient.getQueryData<Product>([CacheKey.CiscoProducts, String(productId)]);
          if (product) {
            store.dispatch(refreshProduct(product));
          }
        }
      },
    }
  );
};

const useDeleteProductFeatureVersionMutation = (
  productId: Product["id"],
  versionId?: ProductVersion["id"]
) => {
  const queryClient = useQueryClient();

  return useDeleteItemMutation<PartialWithId<ProductFeatureVersionLink>>(
    PRODUCT_FEATURE_VERSION_URL,
    (payload) => [CacheKey.ProductFeatureVersion, CacheKey.ProductVersion, versionId, payload.id],
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: [CacheKey.ProductFeatureVersion] });
        await queryClient.invalidateQueries({ queryKey: [CacheKey.ProductVersion, CacheKey.Product, productId] });
        await queryClient.invalidateQueries({ queryKey: [CacheKey.ProductFeature, CacheKey.Product, productId] });
        await queryClient.refetchQueries({ queryKey: [CacheKey.CiscoProducts, String(productId)] });

        const state = store.getState();
        if (state?.products?.isCiscoProductsInitialized) {
          const product = queryClient.getQueryData<Product>([CacheKey.CiscoProducts, String(productId)]);
          if (product) {
            store.dispatch(refreshProduct(product));
          }
        }
      },
    }
  );
};

// Product License
const useFetchLicenses = (params?, options?) =>
  useFetchItems<ProductLicense[]>(
    "db/product-license",
    CacheKey.ProductLicense,
    { ...params, pagination: false },
    { ...options }
  );

const useFetchSingleProductLicense = (
  productId: Product["id"],
  licenseId: ProductLicense["id"],
  params?,
  options?
) =>
  useFetchItem<ProductLicense>(
    "db/product-license",
    licenseId,
    () => [CacheKey.ProductLicense, CacheKey.Product, productId],
    { ...params, detailed: true },
    {
      ...options,
      enabled: typeof options?.enabled === "boolean"
        ? options.enabled && validId(productId) && validId(licenseId)
        : validId(productId) && validId(licenseId),
    }
  );

const useAddProductLicenseMutation = (productId: Product["id"]) => {
  const queryClient = useQueryClient();

  return useAddItemMutation<ProductLicense>(
    "db/product-license",
    () => [CacheKey.ProductLicense, CacheKey.Product, productId],
    {
      onSuccess: async () => {
        await queryClient.refetchQueries({ queryKey: [CacheKey.CiscoProducts, String(productId)] });

        const state = store.getState();
        if (state?.products?.isCiscoProductsInitialized) {
          const product = queryClient.getQueryData<Product>([CacheKey.CiscoProducts, String(productId)]);
          if (product) {
            store.dispatch(refreshProduct(product));
          }
        }
      },
    }
  );
};

const useUpdateProductLicenseMutation = (productId: Product["id"]) => {
  const queryClient = useQueryClient();

  return useUpdateItemMutation<ProductLicense>(
    "db/product-license",
    (payload) => [CacheKey.ProductLicense, CacheKey.Product, productId, payload.id],
    {
      onSuccess: async () => {
        await queryClient.refetchQueries({ queryKey: [CacheKey.CiscoProducts, String(productId)] });

        const state = store.getState();
        if (state?.products?.isCiscoProductsInitialized) {
          const product = queryClient.getQueryData<Product>([CacheKey.CiscoProducts, String(productId)]);
          if (product) {
            store.dispatch(refreshProduct(product));
          }
        }
      },
    }
  );
};

// Product Appliance
const useFetchProductAppliances = (productId: Product["id"], params?, options?) =>
  useFetchItems<ProductAppliance[]>(
    "db/product-appliance",
    () => [CacheKey.ProductAppliance, CacheKey.Product, productId],
    { ...params, product: productId },
    {
      ...options,
      enabled: typeof options?.enabled === "boolean"
        ? options.enabled && validId(productId)
        : validId(productId),
    }
  );

export {
  useFetchSingleProduct,
  useFetchCiscoProducts,
  useAddProductMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,

  useFetchProductComponents,
  useFetchSingleProductComponent,
  useAddProductComponentMutation,
  useUpdateProductComponentMutation,
  useDeleteProductComponentMutation,

  useFetchProductUseCases,
  useFetchSingleProductUseCase,
  useAddProductUseCaseMutation,
  useUpdateProductUseCaseMutation,
  useDeleteProductUseCaseMutation,

  useFetchAllProductFeatures,
  useFetchProductFeatures,
  useFetchSingleProductFeature,
  useAddProductFeatureMutation,
  useUpdateProductFeatureMutation,
  useDeleteProductFeatureMutation,

  useFetchProductFeatureSuccessCriteria,
  useAddProductFeatureSuccessCriteriaMutation,
  useUpdateProductFeatureSuccessCriteriaMutation,
  useDeleteProductFeatureSuccessCriteriaMutation,

  useFetchProductVersions,
  useFetchProductFeatureVersions,
  useFetchProductVersionFeatures,
  useAddProductFeatureVersionMutation,
  useDeleteProductFeatureVersionMutation,

  useFetchLicenses,
  useFetchSingleProductLicense,
  useAddProductLicenseMutation,
  useUpdateProductLicenseMutation,

  useFetchProductAppliances,
};